<script setup>
import { computed, onMounted, ref } from "vue";
import dayjs from "dayjs";
import apiClient from "@/apiClient";
import TeamPerformanceTrendChart from "@/components/TeamPerformanceTrendChart.vue";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

const emptySummary = () => ({
  team_members: 0,
  replies_sent: 0,
  tickets_closed: 0,
  tickets_reopened: 0,
  calls_logged: 0,
  quotes_created: 0,
  quotes_sent: 0,
  quotes_accepted: 0,
  accepted_value_minor: 0,
  invoices_sent: 0,
  invoices_marked_paid: 0,
  staff_allocations: 0,
  vehicle_allocations: 0,
  vehicle_record_updates: 0,
  first_responses: 0,
  median_first_response_seconds: null,
  p90_first_response_seconds: null,
});

const today = dayjs();
const startDate = ref(today.subtract(29, "day").format("YYYY-MM-DD"));
const endDate = ref(today.format("YYYY-MM-DD"));
const selectedUserId = ref("");
const loading = ref(false);
const error = ref("");
const summary = ref(emptySummary());
const users = ref([]);
const userOptions = ref([]);
const trend = ref([]);
const meta = ref({});

const periodLabel = computed(() => {
  if (!startDate.value || !endDate.value) {
    return "Selected period";
  }

  return `${dayjs(startDate.value).format("D MMM YYYY")} – ${dayjs(endDate.value).format("D MMM YYYY")}`;
});

const selectedUser = computed(() =>
  userOptions.value.find(
    (user) => String(user.id) === String(selectedUserId.value),
  ),
);

const reportSubject = computed(
  () => selectedUser.value?.name || "All team members",
);

const formatNumber = (value) =>
  new Intl.NumberFormat("en-GB").format(Number(value ?? 0));
const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: meta.value.currency || "GBP",
    maximumFractionDigits: 0,
  }).format(Number(value ?? 0) / 100);

const formatDuration = (value) => {
  if (value === null || value === undefined) {
    return "No responses";
  }

  const seconds = Math.max(0, Number(value));
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const loadReport = async () => {
  if (!startDate.value || !endDate.value) {
    error.value = "Choose both a start and end date.";
    return;
  }
  if (dayjs(startDate.value).isAfter(dayjs(endDate.value), "day")) {
    error.value = "The start date must be on or before the end date.";
    return;
  }

  loading.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.get("/api/super/team-performance", {
      params: {
        start_date: startDate.value,
        end_date: endDate.value,
        user_id: selectedUserId.value || undefined,
      },
    });
    summary.value = { ...emptySummary(), ...(data?.summary || {}) };
    users.value = data?.users || [];
    userOptions.value = data?.user_options || [];
    trend.value = data?.trend || [];
    meta.value = data?.meta || {};
  } catch (err) {
    error.value =
      err.response?.data?.message ||
      "Unable to load team performance right now.";
  } finally {
    loading.value = false;
  }
};

const usePreset = (days) => {
  endDate.value = dayjs().format("YYYY-MM-DD");
  startDate.value = dayjs()
    .subtract(days - 1, "day")
    .format("YYYY-MM-DD");
  void loadReport();
};

onMounted(() => {
  useBreadcrumbs().setBreadcrumb("Team Performance");
  void loadReport();
});
</script>

<template>
  <div class="space-y-6 text-slate-700 dark:text-slate-200">
    <div
      class="flex flex-col gap-4 border-b border-slate-200 pb-5 dark:border-slate-700 lg:flex-row lg:items-start lg:justify-between"
    >
      <div>
        <p
          class="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-300"
        >
          Admin · Business outcomes
        </p>
        <h1
          class="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-50 md:text-3xl"
        >
          Team Performance
        </h1>
        <p class="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
          Recorded customer work and commercial outcomes for
          {{ reportSubject.toLowerCase() }}. This is a coaching view, not a
          single productivity score.
        </p>
      </div>
      <div
        class="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
      >
        <p class="font-medium">Privacy boundary</p>
        <p class="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
          Page views, online time, IP addresses and message contents are
          excluded.
        </p>
      </div>
    </div>

    <section
      class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <div class="flex flex-wrap items-end gap-3">
        <div>
          <label
            for="performance-start"
            class="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >Start</label
          >
          <input
            id="performance-start"
            v-model="startDate"
            type="date"
            class="input mt-1 border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label
            for="performance-end"
            class="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >End</label
          >
          <input
            id="performance-end"
            v-model="endDate"
            type="date"
            class="input mt-1 border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>
        <div class="min-w-[240px] flex-1">
          <label
            for="performance-user"
            class="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >Team member</label
          >
          <select
            id="performance-user"
            v-model="selectedUserId"
            class="input mt-1 w-full border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="">All team members</option>
            <option
              v-for="user in userOptions"
              :key="user.id"
              :value="String(user.id)"
            >
              {{ user.name }}{{ user.active ? "" : " (inactive)" }}
            </option>
          </select>
        </div>
        <button
          type="button"
          class="h-10 rounded-lg bg-cyan-600 px-5 font-medium text-white transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-cyan-500 dark:text-slate-950 dark:hover:bg-cyan-400"
          :disabled="loading"
          @click="loadReport"
        >
          {{ loading ? "Loading…" : "Apply" }}
        </button>
      </div>
      <div class="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span class="text-slate-400">Quick range:</span>
        <button
          type="button"
          class="rounded-full border border-slate-200 px-3 py-1 text-slate-600 transition hover:border-cyan-400 hover:bg-cyan-50 hover:text-cyan-700 dark:border-slate-600 dark:text-slate-300 dark:hover:border-cyan-500 dark:hover:bg-slate-700 dark:hover:text-cyan-300"
          @click="usePreset(7)"
        >
          7 days
        </button>
        <button
          type="button"
          class="rounded-full border border-slate-200 px-3 py-1 text-slate-600 transition hover:border-cyan-400 hover:bg-cyan-50 hover:text-cyan-700 dark:border-slate-600 dark:text-slate-300 dark:hover:border-cyan-500 dark:hover:bg-slate-700 dark:hover:text-cyan-300"
          @click="usePreset(30)"
        >
          30 days
        </button>
        <button
          type="button"
          class="rounded-full border border-slate-200 px-3 py-1 text-slate-600 transition hover:border-cyan-400 hover:bg-cyan-50 hover:text-cyan-700 dark:border-slate-600 dark:text-slate-300 dark:hover:border-cyan-500 dark:hover:bg-slate-700 dark:hover:text-cyan-300"
          @click="usePreset(90)"
        >
          90 days
        </button>
        <span class="ml-auto text-slate-400">{{ periodLabel }}</span>
      </div>
    </section>

    <div
      v-if="error"
      role="alert"
      class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
    >
      {{ error }}
    </div>

    <section
      class="grid gap-4 sm:grid-cols-2 xl:grid-cols-6"
      :class="{ 'opacity-60': loading }"
    >
      <article
        class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <p
          class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Replies sent
        </p>
        <p class="mt-2 text-2xl font-semibold text-cyan-700 dark:text-cyan-300">
          {{ formatNumber(summary.replies_sent) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">Customer responses</p>
      </article>
      <article
        class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <p
          class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Tickets closed
        </p>
        <p
          class="mt-2 text-2xl font-semibold text-emerald-700 dark:text-emerald-300"
        >
          {{ formatNumber(summary.tickets_closed) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">
          {{ formatNumber(summary.tickets_reopened) }} reopened
        </p>
      </article>
      <article
        class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <p
          class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          First response
        </p>
        <p
          class="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100"
        >
          {{ formatDuration(summary.median_first_response_seconds) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">
          Median · P90 {{ formatDuration(summary.p90_first_response_seconds) }}
        </p>
      </article>
      <article
        class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <p
          class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Quotes sent
        </p>
        <p
          class="mt-2 text-2xl font-semibold text-indigo-700 dark:text-indigo-300"
        >
          {{ formatNumber(summary.quotes_sent) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">
          {{ formatNumber(summary.quotes_created) }} created
        </p>
      </article>
      <article
        class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <p
          class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Quotes accepted
        </p>
        <p
          class="mt-2 text-2xl font-semibold text-violet-700 dark:text-violet-300"
        >
          {{ formatNumber(summary.quotes_accepted) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">Attributed to quote owner</p>
      </article>
      <article
        class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <p
          class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Accepted value
        </p>
        <p
          class="mt-2 text-2xl font-semibold text-amber-700 dark:text-amber-300"
        >
          {{ formatCurrency(summary.accepted_value_minor) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">Accepted in period</p>
      </article>
    </section>

    <section
      class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      :class="{ 'opacity-60': loading }"
    >
      <div class="mb-4">
        <p
          class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Operational actions
        </p>
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Recorded workflow completed
        </h2>
      </div>
      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <article
          class="rounded-xl border border-sky-100 bg-sky-50/70 p-4 dark:border-sky-900/60 dark:bg-sky-950/30"
        >
          <p
            class="text-xs uppercase tracking-wide text-sky-700 dark:text-sky-300"
          >
            Invoices sent
          </p>
          <p class="mt-2 text-2xl font-semibold text-sky-900 dark:text-sky-100">
            {{ formatNumber(summary.invoices_sent) }}
          </p>
          <p class="mt-1 text-xs text-sky-700/70 dark:text-sky-300/70">
            Emailed to customers
          </p>
        </article>
        <article
          class="rounded-xl border border-teal-100 bg-teal-50/70 p-4 dark:border-teal-900/60 dark:bg-teal-950/30"
        >
          <p
            class="text-xs uppercase tracking-wide text-teal-700 dark:text-teal-300"
          >
            Marked paid
          </p>
          <p
            class="mt-2 text-2xl font-semibold text-teal-900 dark:text-teal-100"
          >
            {{ formatNumber(summary.invoices_marked_paid) }}
          </p>
          <p class="mt-1 text-xs text-teal-700/70 dark:text-teal-300/70">
            Invoice payments recorded
          </p>
        </article>
        <article
          class="rounded-xl border border-orange-100 bg-orange-50/70 p-4 dark:border-orange-900/60 dark:bg-orange-950/30"
        >
          <p
            class="text-xs uppercase tracking-wide text-orange-700 dark:text-orange-300"
          >
            Staff allocations
          </p>
          <p
            class="mt-2 text-2xl font-semibold text-orange-900 dark:text-orange-100"
          >
            {{ formatNumber(summary.staff_allocations) }}
          </p>
          <p class="mt-1 text-xs text-orange-700/70 dark:text-orange-300/70">
            Assignments and reassignments
          </p>
        </article>
        <article
          class="rounded-xl border border-violet-100 bg-violet-50/70 p-4 dark:border-violet-900/60 dark:bg-violet-950/30"
        >
          <p
            class="text-xs uppercase tracking-wide text-violet-700 dark:text-violet-300"
          >
            Vehicle allocations
          </p>
          <p
            class="mt-2 text-2xl font-semibold text-violet-900 dark:text-violet-100"
          >
            {{ formatNumber(summary.vehicle_allocations) }}
          </p>
          <p class="mt-1 text-xs text-violet-700/70 dark:text-violet-300/70">
            Allocations and changes
          </p>
        </article>
        <article
          class="rounded-xl border border-rose-100 bg-rose-50/70 p-4 dark:border-rose-900/60 dark:bg-rose-950/30"
        >
          <p
            class="text-xs uppercase tracking-wide text-rose-700 dark:text-rose-300"
          >
            Vehicle updates
          </p>
          <p
            class="mt-2 text-2xl font-semibold text-rose-900 dark:text-rose-100"
          >
            {{ formatNumber(summary.vehicle_record_updates) }}
          </p>
          <p class="mt-1 text-xs text-rose-700/70 dark:text-rose-300/70">
            Fleet records changed
          </p>
        </article>
      </div>
    </section>

    <section
      class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <div class="mb-4">
        <p
          class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Trend
        </p>
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Recorded work by day
        </h2>
      </div>
      <div
        v-if="loading"
        class="flex h-72 items-center justify-center text-sm text-slate-400"
      >
        Loading activity trend…
      </div>
      <TeamPerformanceTrendChart v-else-if="trend.length" :entries="trend" />
      <div
        v-else
        class="flex h-40 items-center justify-center text-sm text-slate-400"
      >
        No recorded work in this period.
      </div>
    </section>

    <section
      class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <div
        class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700"
      >
        <div>
          <p
            class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Team detail
          </p>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Outcome scorecard
          </h2>
        </div>
        <span
          class="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-200"
        >
          {{ summary.team_members }}
          {{ summary.team_members === 1 ? "member" : "members" }}
        </span>
      </div>

      <div v-if="loading" class="px-5 py-8 text-center text-sm text-slate-400">
        Loading team scorecard…
      </div>
      <div
        v-else-if="!users.length"
        class="px-5 py-8 text-center text-sm text-slate-400"
      >
        No eligible team members or recorded work found.
      </div>
      <div v-else class="overflow-x-auto">
        <table
          class="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700"
        >
          <thead
            class="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/60 dark:text-slate-400"
          >
            <tr>
              <th class="px-5 py-3 font-medium">Team member</th>
              <th class="px-4 py-3 text-right font-medium">Replies</th>
              <th class="px-4 py-3 text-right font-medium">Closed</th>
              <th class="px-4 py-3 text-right font-medium">Calls</th>
              <th class="px-4 py-3 text-right font-medium">Reopened</th>
              <th class="px-4 py-3 text-right font-medium">First response</th>
              <th class="px-4 py-3 text-right font-medium">Quotes C / S / A</th>
              <th class="px-4 py-3 text-right font-medium">Invoices S / P</th>
              <th class="px-4 py-3 text-right font-medium">Staff allocated</th>
              <th class="px-4 py-3 text-right font-medium">Vehicles A / U</th>
              <th class="px-5 py-3 text-right font-medium">Accepted value</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
            <tr
              v-for="user in users"
              :key="user.id"
              class="hover:bg-slate-50/70 dark:hover:bg-slate-900/30"
            >
              <td class="px-5 py-4">
                <div class="font-medium text-slate-900 dark:text-slate-100">
                  {{ user.name }}
                </div>
                <div class="text-xs text-slate-400">{{ user.email }}</div>
                <span
                  v-if="!user.active"
                  class="mt-1 inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500 dark:bg-slate-700 dark:text-slate-300"
                  >Inactive</span
                >
              </td>
              <td
                class="px-4 py-4 text-right tabular-nums text-slate-700 dark:text-slate-200"
              >
                {{ formatNumber(user.replies_sent) }}
              </td>
              <td
                class="px-4 py-4 text-right tabular-nums text-slate-700 dark:text-slate-200"
              >
                {{ formatNumber(user.tickets_closed) }}
              </td>
              <td
                class="px-4 py-4 text-right tabular-nums text-slate-700 dark:text-slate-200"
              >
                {{ formatNumber(user.calls_logged) }}
              </td>
              <td
                class="px-4 py-4 text-right tabular-nums"
                :class="
                  user.tickets_reopened
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-slate-400 dark:text-slate-500'
                "
              >
                {{ formatNumber(user.tickets_reopened) }}
              </td>
              <td class="px-4 py-4 text-right">
                <div class="tabular-nums text-slate-700 dark:text-slate-200">
                  {{ formatDuration(user.median_first_response_seconds) }}
                </div>
                <div
                  v-if="user.p90_first_response_seconds !== null"
                  class="text-xs text-slate-400"
                >
                  P90 {{ formatDuration(user.p90_first_response_seconds) }}
                </div>
              </td>
              <td
                class="px-4 py-4 text-right tabular-nums text-slate-700 dark:text-slate-200"
              >
                {{ user.quotes_created }} / {{ user.quotes_sent }} /
                {{ user.quotes_accepted }}
              </td>
              <td
                class="px-4 py-4 text-right tabular-nums text-slate-700 dark:text-slate-200"
              >
                {{ user.invoices_sent }} / {{ user.invoices_marked_paid }}
              </td>
              <td
                class="px-4 py-4 text-right tabular-nums text-slate-700 dark:text-slate-200"
              >
                {{ formatNumber(user.staff_allocations) }}
              </td>
              <td
                class="px-4 py-4 text-right tabular-nums text-slate-700 dark:text-slate-200"
              >
                {{ user.vehicle_allocations }} /
                {{ user.vehicle_record_updates }}
              </td>
              <td
                class="px-5 py-4 text-right font-medium tabular-nums text-slate-900 dark:text-slate-100"
              >
                {{ formatCurrency(user.accepted_value_minor) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section
      class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300"
    >
      <p class="font-medium text-slate-700 dark:text-slate-100">
        How these figures work
      </p>
      <p class="mt-1">
        First response is measured from ticket creation to the first outbound
        staff reply. Automated quote-chasing messages are excluded. Ticket
        actions use the immutable ticket history. Quote counts and accepted
        value are attributed to the quote owner. Invoice sends, payments and
        staff allocations, vehicle allocations and vehicle record updates use
        authenticated reference-history entries; automated actions without a
        user are not credited. Figures should be interpreted alongside workload
        and case complexity.
      </p>
    </section>
  </div>
</template>
